import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Modal } from "@mui/material";
import { XIcon } from "@heroicons/react/solid";
import SalaryBreakupTable from "./SalaryBreakupTable";
import { SALARY_BREAKUP_SECTIONS } from "./salaryBreakupConfig";
import { normalizeSalaryBreakupData } from "./salaryBreakupUtils";

/**
 * Modal dialog for Old Scheme Salary Breakup comparison matrix.
 *
 * Follows Single Responsibility Principle (SRP):
 * Manages modal visibility, accessibility lifecycle, and high-level layout,
 * delegating table presentation to SalaryBreakupTable.
 */
const SalaryBreakupModal = ({
  isOpen,
  onClose,
  breakupData,
  employeeName,
  monthYear,
}) => {
  // Normalize and memoize columns to prevent unnecessary recalculations
  const columns = useMemo(() => {
    return normalizeSalaryBreakupData(breakupData);
  }, [breakupData]);

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      aria-labelledby="salary-breakup-modal-title"
      aria-describedby="salary-breakup-modal-description"
    >
      <div className="w-[94%] md:w-[88%] lg:w-[80%] xl:w-[72%] 2xl:w-[65%] max-h-[90vh] bg-white rounded-lg shadow-2xl absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col focus:outline-none overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
          <div>
            <h3
              id="salary-breakup-modal-title"
              className="text-lg font-bold text-gray-800"
            >
              Salary Breakup Details
            </h3>
            {(employeeName || monthYear) && (
              <p
                id="salary-breakup-modal-description"
                className="text-xs text-gray-500 mt-1"
              >
                {employeeName && (
                  <span>
                    Employee:{" "}
                    <strong className="text-gray-700">{employeeName}</strong>
                  </span>
                )}
                {employeeName && monthYear && <span> &bull; </span>}
                {monthYear && (
                  <span>
                    Period: <strong className="text-gray-700">{monthYear}</strong>
                  </span>
                )}
                {columns.length > 0 && (
                  <>
                    <span> &bull; </span>
                    <span>
                      Breakup Count:{" "}
                      <strong className="text-blue-700">{columns.length}</strong>
                    </span>
                  </>
                )}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-800 hover:bg-gray-200 transition-colors"
            title="Close"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body - Dynamic Breakup Matrix Table */}
        <div className="p-6 overflow-y-auto flex-1">
          <SalaryBreakupTable
            columns={columns}
            sections={SALARY_BREAKUP_SECTIONS}
          />
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-1.5 text-xs font-semibold rounded bg-gray-200 hover:bg-gray-300 text-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

SalaryBreakupModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  breakupData: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.object),
    PropTypes.object,
  ]),
  employeeName: PropTypes.string,
  monthYear: PropTypes.string,
};

SalaryBreakupModal.defaultProps = {
  breakupData: null,
  employeeName: "",
  monthYear: "",
};

export default React.memo(SalaryBreakupModal);
